import Skeleton from 'react-loading-skeleton';
import { AIECGViewer, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { getSelectedEventType } from '@/entities/event';
import { RHYTHM_EVENT_TAB_ID } from '@/entities/report';
import useEcgViewerWrapper from './useEcgViewerWrapper';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
};

export type EcgViewerWrapperProps = {
  ecgDataMap: any;
  studyId: string;
  profileId: string;
  timezoneOffset: number;
  activeButton: any;
  isProcessBeatEvent?: boolean;
  handleClickArrowButton: (isNext: boolean) => void;
};

/**
 * Hosts the ECG waveform viewer for the rhythm events tab. Draws a three-part skeleton while the strip, heart-rate chart or its options are still loading or no samples arrived, otherwise mounts AIECGViewer with the tab's canvas element ids, the event and bookmark indicators, capture and beat-update handlers, the strip's start moment and timezone offset, and starts in AR beat mode unless a date pick is in progress.
 *
 * @param props - Carries the ECG data map, study and profile ids, timezone offset and the beat-event processing flag.
 * @returns The ECG viewer, or its loading skeleton while data is still arriving.
 */
const EcgViewerWrapper = ({
  ecgDataMap,
  studyId,
  profileId,
  timezoneOffset,
  activeButton,
  isProcessBeatEvent,
  handleClickArrowButton,
}: EcgViewerWrapperProps) => {
  const {
    isLoading,
    isLoadingHrChart,
    isLoadingHrChartOptions,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    handlePickDate,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    getCaptureMode,
    handleCaptureImages,
    clickArrowButtonHandle,
    handleUpdateBeat,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    isPickingDate,
    selectedEvent,
  } = useEcgViewerWrapper({
    ecgDataMap,
    studyId,
    profileId,
    timezoneOffset,
    activeButton,
    handleClickArrowButton,
  });

  if (
    isLoading ||
    isLoadingHrChartOptions ||
    isLoadingHrChart ||
    !ecgObject?.dataSamples?.length ||
    !startStripEpoch.current
  ) {
    return (
      <div id="ecg-viewer" className="ecg-viewer-container">
        <div className="ecg-viewer-skeleton-container">
          <div className="scroller-container">
            <Skeleton height="100%" />
          </div>
          <div className="chart-container">
            <Skeleton height="100%" />
          </div>
          <div className="menu-container">
            <Skeleton height="100%" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ecg-viewer" className="ecg-viewer-container">
      <AIECGViewer
        ref={ecgChartRef}
        idComponents={{
          canvasBackground: 'cvBgRhythmEvents',
          exportGridCanvas: 'expGridCvRhythmEvents',
          canvasChart: 'cvChartRhythmEvents',
          canvasObject: 'cvObjRhythmEvents',
          canvasScroller: 'cvScrollerRhythmEvents',
          contextMenu: 'contextMenuRhythmEvents',
          tooltip: 'tooltipRhythmEvents',
          tab: RHYTHM_EVENT_TAB_ID,
        }}
        ecg={ecgObject}
        ecgDataMap={ecgDataMap}
        configChart={configChart}
        isAppliedAI
        isDisableAIAssist
        isHideFineGridMenu
        isHideMarkASampleMenu
        hasArrowButtonScroller
        hasUpdateBeatContextMenu
        handlePickDate={handlePickDate}
        pickDateAction={pickDateAction}
        eventIndicator={eventIndicator}
        bookmarkIndicators={bookmarkIndicators}
        captureMode={getCaptureMode()}
        handleCaptureImages={handleCaptureImages}
        handleClickArrowButton={clickArrowButtonHandle}
        handleUpdateBeat={handleUpdateBeat}
        timeObject={{
          startMoment: startStripEpoch.current,
          timezoneOffset,
        }}
        isInitialARBeatMode={
          !isPickingDate && (isProcessBeatEvent || ECG_VIEWER_CONFIG_STORE.isInitialARBeatMode)
        }
        aiChannel={aiChannel}
        isDisableNextBtn={isDisableNextBtn}
        isDisablePrevBtn={isDisablePrevBtn}
        studyInfoObject={{
          studyId,
          profileId,
          type: getSelectedEventType(selectedEvent),
        }}
      />
    </div>
  );
};

export default EcgViewerWrapper;
